import VeeValidate, { Validator } from 'vee-validate'
import { DEFAULT_LOCALE, EXCEL_CELL_ERROR_LABEL } from '@/helpers/enums/params'
import moment from 'moment/moment'


const i18nRootKey = 'validation'

const ConfirmByValidate = {
  getMessage: (messages, field, args) => messages.confirmed(field, [args?.field]),
  validate: (value, data) => String(value) === String(data?.value),
}

const formulaValidate = {
  getMessage: () => 'Ошибка в формуле',
  validate: (value) => !value || !value.includes(EXCEL_CELL_ERROR_LABEL),
}

const regexValidate = {
  getMessage: (messages, field, args) => (
    Array.isArray(args)
      ? args[1]
      : 'Ошибка проверки'),
  validate: (value, params) => (
    Array.isArray(params)
      ? params[0].test(value)
      : params.test(value)
  ),
}

const relativeUrlValidate = {
  getMessage: () => 'Ссылка не может начинаться с символа \'/\'',
  validate: (value) => /^(?!\/).*$/.test(value),
}

const uniqueValidate = {
  getMessage: () => 'Поле имеет неуникальное значение',
  validate: (value, data) => {
    if (!value || !data) return true

    const stringValue = value.toString()
    let valueInArrayCounter = 0
    const regex = /^\d+(\.\d+)?$/

    // eslint-disable-next-line no-restricted-syntax, guard-for-in
    for (const columnValue of data) {
      if (columnValue.toString() === stringValue || (regex.test(columnValue) && (columnValue.toString() === stringValue.replace('.', ',') || columnValue.toString() === stringValue.replace(',', '.')))) {
        valueInArrayCounter += 1
        if (valueInArrayCounter > 1) {
          return false
        }
      }
    }
    return true
  },
}

const ruDecimal = {
  getMessage: () => 'Значение не является числом',
  validate: (value) => {
    let v = value
    if (typeof value === 'string') {
      v = value.replace(',', '.')
      const regex = /^\d+(\.\d+)?$/
      if (!regex.test(v) || /^0\d+/.test(v) || (v.endsWith('0') && v.includes('.'))) {
        return false
      }
    }
    return Number(v) || value === 0 || value === '0'
  },
}

const lettersOnly = {
  getMessage: () => 'Поле может содержать только символы',
  validate: (value) => {
    const val = typeof value === 'string' ? value.trim() : value?.toString()
    return /^[A-Za-zА-Яа-яёЁ]+$/u.test(val)
  },
}

const afterTwoDates = {
  getMessage: (messages, field, {
    first, firstField, second, secondField,
  }) => {
    const firstTitle = firstField || 'Первая дата'
    const secondTitle = secondField || 'Вторая дата'
    let str
    if (first && second) {
      str = moment(first).isAfter(moment(second)) ? firstTitle : secondTitle
    } else if (first) {
      str = firstTitle
    } else {
      str = secondTitle
    }
    return `Поле ${field} должно быть позже \u00ab${str}\u00bb`
  },
  validate: (rawValue, { value, first, second }) => {
    if ((!first && !second) || (!value && !rawValue)) {
      return true
    }

    const momentValue = moment(value || rawValue)

    return (first ? moment(first).isBefore(momentValue) : true)
        && (second ? moment(second).isBefore(momentValue) : true)
  },
}

Validator.extend('ruDecimal', ruDecimal)
Validator.extend('confirmBy', ConfirmByValidate, ['confirmBy'])
Validator.extend('formula', formulaValidate)
Validator.extend('regex', regexValidate)
Validator.extend('relativeUrl', relativeUrlValidate)
Validator.extend('unique', uniqueValidate)
Validator.extend('afterBoth', afterTwoDates)
Validator.extend('lettersOnly', lettersOnly)

const dict = {
  ru: {
    custom: {
      topic_ids: {
        required: 'Необходимо выбрать хотя бы один раздел',
      },
    },
  },
}

export const initPlugin = ({ Vue }) => {
  import(
    /* webpackChunkName: "vee-validate.locale.ru" */
    'vee-validate/dist/locale/ru'
  )
    .then((data) => {
      // eslint-disable-next-line object-curly-newline
      const { min_value, max_value, between, regex } = data.messages

      // eslint-disable-next-line no-param-reassign
      data.messages.min_value = (...args) => min_value(...args).replace(/^Поле/, 'Значение')

      // eslint-disable-next-line no-param-reassign
      data.messages.max_value = (...args) => max_value(...args).replace(/^Поле/, 'Значение')

      // eslint-disable-next-line no-param-reassign
      data.messages.between = (...args) => between(...args).replace(/^Поле/, 'Значение')

      // eslint-disable-next-line no-param-reassign
      data.messages.regex = (field, args, options) => {
        const [regex_2, message] = Array.isArray(args) ? args : [args]
        const defaultMessage = regex(field, regex_2, options)

        return [defaultMessage, message].filter(Boolean).join(' ')
      }

      // eslint-disable-next-line no-param-reassign
      data.messages.ruDecimal = ruDecimal.getMessage.bind(null, data.messages)
      // eslint-disable-next-line no-param-reassign
      data.messages.lettersOnly = lettersOnly.getMessage.bind(null, data.messages)
      // eslint-disable-next-line no-param-reassign
      data.messages.confirmBy = ConfirmByValidate.getMessage.bind(null, data.messages)
      // eslint-disable-next-line no-param-reassign
      data.messages.formula = formulaValidate.getMessage.bind(null, data.messages)
      // eslint-disable-next-line no-param-reassign
      data.messages.regex = regexValidate.getMessage.bind(null, data.messages)
      // eslint-disable-next-line no-param-reassign
      data.messages.relativeUrl = relativeUrlValidate.getMessage.bind(null, data.messages)
      // eslint-disable-next-line no-param-reassign
      data.messages.unique = uniqueValidate.getMessage.bind(null, data.messages)
      // eslint-disable-next-line no-param-reassign
      data.messages.afterBoth = afterTwoDates.getMessage.bind(null, data.messages)

      Validator.localize(dict)
      Validator.localize('ru', data)
      return null
    })
    .catch(() => null)

  Vue.use(VeeValidate, {
    behavior: 'eager',
    events: 'change',
    i18nRootKey,
    inject: false,
    errorBagName: '$errors',
    fieldsBagName: '$fields',
    locale: DEFAULT_LOCALE,
    strict: process.env.NODE_ENV !== 'production',
  })
}
